/**
 * Onboarding Step Labels — Sprint A / Integration Pass
 * ─────────────────────────────────────────────────────
 * Single source for human-readable step labels.
 *
 * Required by the integration pass: reupload banners and admin
 * surfaces MUST NEVER show internal registry keys like
 * `inherited_compliance_preview` or `parent_confirmation`. They use
 * `getOnboardingStepLabel()` to resolve a clean title.
 *
 * Resolution priority:
 *   1. Entity-aware title override from the step registry
 *      (`getOnboardingSteps(session)` already applies overrides).
 *   2. Base descriptor title from the registry.
 *   3. Defensive humanization of the raw key (`under_score → spaced`).
 *      Should never be reached for known keys.
 */

import {
  getOnboardingSteps,
  type OnboardingStepKey,
} from "./stepRegistry";
import type { OnboardingSession } from "./types";

function humanize(key: string): string {
  return key
    .split("_")
    .map((p) => (p.length === 0 ? p : p[0].toUpperCase() + p.slice(1)))
    .join(" ");
}

/**
 * Resolve a human label for a step. Pass `session` when an
 * entity-aware override is desired (e.g. `documents` →
 * "Employment documents" for `internal_w2`).
 */
export function getOnboardingStepLabel(
  stepKey: string,
  session?: Pick<OnboardingSession, "entityType">,
): string {
  if (session) {
    const descriptor = getOnboardingSteps(session).find(
      (s) => s.key === stepKey,
    );
    if (descriptor) return descriptor.title;
  }
  // Fall back to a registry-agnostic base label for any known key.
  const baseKey = stepKey as OnboardingStepKey;
  switch (baseKey) {
    case "identity": return "Identity & contact";
    case "service_area": return "Service area & trade";
    case "parent_confirmation": return "Parent entity confirmation";
    case "inherited_compliance_preview": return "Inherited compliance";
    case "branch_overrides": return "Branch overrides";
    case "minimal_info": return "A few details";
    case "documents": return "Compliance documents";
    case "agreement": return "Agreement & signature";
    case "payout": return "Payout method";
    case "review": return "Review & submit";
    default:
      return humanize(stepKey);
  }
}

/** Convenience: format a list of step keys as a comma-joined label list. */
export function formatOnboardingStepKeys(
  stepKeys: readonly string[],
  session?: Pick<OnboardingSession, "entityType">,
): string {
  return stepKeys.map((k) => getOnboardingStepLabel(k, session)).join(", ");
}
